import { FC } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { HintTooltip } from "@/components/Interface/HintTooltip/HintTooltip";
import { ESimInfoEsimResetNotAllowedReasonItem } from "@/services/model";

type PossibleReasons = ESimInfoEsimResetNotAllowedReasonItem;

interface Props {
  dataTestid: string;
  disabledButtonText: string;
  reasons: PossibleReasons[];
}

// The following tooltip is needed to let the user know why the functionality in their reload/transfer button
// is disabled. It reads from an array of reasons from the BE endpoint EsimInfoEsimActionNotAllowed and once the user
// and appears once the user tired clicking on the buttons, thus helping them know why the function is unavailable.
export const EsimDisabledActionsTooltip: FC<Props> = ({
  dataTestid,
  disabledButtonText,
  reasons,
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "cockpit.managementTile.details.sections.details",
  });

  // Reasons from api
  const {
    SimSuspended,
    SimInactive,
    NoMoreActions,
    WrongSimType,
    NoAutoRenew,
    SimNoActivePackage,
    SimTerminated,
    SimCancelled,
    StatusNotAllowed,
    SimFixedService,
    PackageOrderInProgress,
  } = ESimInfoEsimResetNotAllowedReasonItem;

  const mapReasonsToTranslation = (reasons: PossibleReasons[]) => {
    return reasons
      .map((reason) => {
        switch (reason) {
          case SimSuspended:
            return t("reasons.simSuspended");
          case SimInactive:
            return t("reasons.simInactive");
          case NoMoreActions:
            return t("reasons.noMoreActions");
          case WrongSimType:
            return t("reasons.wrongSimType");
          case NoAutoRenew:
            return t("reasons.noAutoRenew");
          case SimNoActivePackage:
            return t("reasons.simNoActivePackage");
          case SimTerminated:
          case SimCancelled:
            return t("reasons.simTerminated");
          case StatusNotAllowed:
            return t("reasons.StatusNotAllowed");
          case SimFixedService:
            return t("reasons.simFixedService");
          case PackageOrderInProgress:
            return t("reasons.packageOrderInProgress");
          default:
            return "";
        }
      })
      .filter((reason) => reason.length);
  };
  const translatedReasons = mapReasonsToTranslation(reasons);

  return (
    <HintTooltip
      dataTestid={`${dataTestid}-hint`}
      align="right"
      content={
        <>
          <span className="mb-1 block font-semibold">
            {t("portal:common.tooltip.actionNotPossible.title")}
          </span>
          <span className="block">
            {t("portal:common.tooltip.actionNotPossible.text", {
              count: translatedReasons.length,
            })}
          </span>
          {!!translatedReasons.length && (
            <ul className="mt-1 list-outside list-disc pl-4">
              {translatedReasons.map((reason: string) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          )}
        </>
      }
    >
      <Button dataTestid={dataTestid} size="sm" variant="primary" disabled>
        {disabledButtonText}
      </Button>
    </HintTooltip>
  );
};
